import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import OpacityIcon from "@mui/icons-material/Opacity";
import SpeedIcon from "@mui/icons-material/Speed";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import { alpha, useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FuelCellSummaryStats } from "../models/FuelCellRow";

interface Props {
  stats: FuelCellSummaryStats;
}

interface TileProps {
  label: string;
  value: string;
  hint?: string;
  icon: ReactNode;
  color: string;
}

function Tile({ label, value, hint, icon, color }: TileProps) {
  const theme = useTheme();
  return (
    <Card sx={{ flex: 1, minWidth: 180 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(color, theme.palette.mode === "dark" ? 0.25 : 0.12),
              color,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" noWrap>
              {label}
            </Typography>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }} noWrap>
              {value}
            </Typography>
            {hint && (
              <Typography variant="caption" color="text.secondary" noWrap>
                {hint}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

export function FuelCellSummary({ stats }: Props) {
  const { t } = useTranslation();
  const theme = useTheme();

  const fmtKg = (v: number) => `${v.toFixed(1)} kg`;
  const fmtPct = (v: number | null) => (v == null ? "—" : `${v.toFixed(1)} %`);

  return (
    <Stack direction="row" gap={1.5} flexWrap="wrap" sx={{ px: 2, flexShrink: 0 }}>
      <Tile
        label={t("fuelCell.summary.totalUsed", "Total used")}
        value={fmtKg(stats.totalUsedKg)}
        hint={t("fuelCell.summary.acrossVehicles", "Across {{count}} vehicles", {
          count: stats.deviceCount,
        })}
        icon={<SpeedIcon />}
        color={theme.palette.primary.main}
      />
      <Tile
        label={t("fuelCell.summary.totalRefills", "Refills")}
        value={String(stats.totalRefills)}
        hint={t("fuelCell.summary.inRange", "In selected range")}
        icon={<LocalGasStationIcon />}
        color={theme.palette.success.main}
      />
      <Tile
        label={t("fuelCell.summary.avgLevel", "Average level")}
        value={fmtPct(stats.avgLevelPercent)}
        hint={t("fuelCell.summary.currentSnapshot", "Current snapshot")}
        icon={<OpacityIcon />}
        color={theme.palette.info.main}
      />
      <Tile
        label={t("fuelCell.summary.lowFuel", "Low fuel")}
        value={String(stats.lowFuelCount)}
        hint={t("fuelCell.summary.below20", "Below 20 %")}
        icon={<WarningAmberIcon />}
        color={stats.lowFuelCount > 0 ? theme.palette.warning.main : theme.palette.text.secondary}
      />
    </Stack>
  );
}
